import { useEffect } from "react";
import useAuth from "src/hooks/useAuth";
import { useDispatch, useSelector } from "src/redux/store";
import { fetchOnboardingProgress } from "src/redux/slices/onboarding";
import { useClaims } from "src/hooks/useClaims";

/**
 * Hook para consumir el progreso del wizard onboarding v2. Auto-carga
 * al montar si el user está autenticado y aún no se pidió. NO recarga
 * al cambiar de ruta (para no golpear la API en cada navegación).
 *
 * Devuelve: { progress, loading, isVisible, shouldAutoOpen, isNewUser, isMandatory }
 *   • progress: DtoOnboardingProgress | null
 *   • loading: bool
 *   • isNewUser: user acaba de registrarse, aún no tiene datos reales
 *     (sin mesas creadas) y aún no ha completado ni un step del
 *     wizard. Es al que le hacemos el wizard OBLIGATORIO.
 *   • isMandatory: alias de `isNewUser && !isDone` — para gates de UI.
 *   • isVisible: el chip del TopBar debe mostrarse. Se muestra si:
 *     - onboarding no está completo Y
 *     - es un user nuevo (sin mesas) O ya empezó el wizard.
 *     Users antiguos con mesas creadas NO ven el chip aunque
 *     `progress.isDone` sea false (no queremos interrumpirles).
 *   • shouldAutoOpen: el modal debe abrirse solo al aterrizar. True
 *     si `isVisible` y (`isMandatory` o `!dismissedForToday`). El
 *     "Recordar mañana" NO aplica a users nuevos — el wizard es
 *     obligatorio para que puedan trabajar.
 */
export default function useOnboardingProgress() {
  const dispatch = useDispatch();
  const { isAuthenticated, user } = useAuth();
  const { progress, loading } = useSelector((s) => s.onboarding.wizard);
  // Estado del gate legal (documentos pendientes de firma). El wizard NO
  // debe auto-abrirse mientras el LegalOnboardingGate está bloqueando la
  // UI — apilar dos modales confunde y el usuario ve dos flujos a la vez.
  // El wizard sí se puede abrir manualmente desde el chip del TopBar
  // (isVisible sigue siendo true), sólo evitamos el auto-open.
  const legalCompleted = useSelector((s) => s.onboarding.isCompleted);
  const legalPending = useSelector((s) => s.onboarding.pending);
  const legalLoaded = useSelector((s) => s.onboarding.isLoaded);
  const blockedByLegal = legalLoaded
    && !legalCompleted
    && Array.isArray(legalPending)
    && legalPending.length > 0;
  const claims = useClaims();

  // El wizard SOLO aplica a licencias puras (USER_NORMAL / USER_NORMAL_PRO).
  // Admins de plataforma, franquiciados, franquicias, comerciales,
  // distribuidores, empleados, etc. NO deben ver el chip ni el modal
  // — tienen su propia interfaz y no necesitan configurar un TPV.
  //
  // Además: si es un empleado logueado por web (sesión personal),
  // aunque el JWT herede la identidad del restaurante padre (`role`
  // = UserNormal), NUNCA le mostramos el wizard — el onboarding es
  // exclusivo del owner. La flag viene del thunk `login` cuando
  // `res.data.employeeId != null`.
  const isEligibleForWizard =
    (claims.isUserNormal || claims.isUserNormalPro)
    && !claims.isEmployeeWebSession;

  useEffect(() => {
    if (!isEligibleForWizard) return;
    if (!isAuthenticated || !user || progress !== null) return;
    dispatch(fetchOnboardingProgress());
  }, [isAuthenticated, user, progress, dispatch, isEligibleForWizard]);

  // 2026-08-27 · Veteranía calculada en backend (accountAge>30d + mesas
  // + categorías + productos). Los usuarios veteranos NO ven el wizard:
  // ni chip, ni auto-open, ni "checkout pendiente" bloqueante. Su
  // licencia lleva tiempo en uso — si no tienen plan es otra decisión
  // comercial que se resuelve fuera del wizard (recordatorio Stripe,
  // aviso admin), no bloqueando la UI operativa. Sin este flag, la
  // combinación `progress.isDone=true + sin sub` disparaba
  // `hasPendingCheckout=true` y el wizard aparecía 100% completado
  // pero sin escape porque `isMandatory=true`.
  const isVeteran = !!progress?.isVeteran;

  const isNotDone = !!progress && !progress.isDone;
  const alreadyStarted = (progress?.completedSteps || []).length > 0;
  const completed = progress?.completedSteps || [];
  // 2026-07-28 · wizard reducido a 7 steps con tabs internas: los
  // codes de facturación son `subscription` + `checkout`. Mantenemos
  // aceptados los legacy (plan/addons/support) por si algún user
  // antiguo tiene el progress con esos códigos.
  const hasStartedBilling = completed.some((s) =>
    ["subscription", "checkout", "plan", "addons", "support"].includes(s),
  );

  // ─── Checkout pendiente ────────────────────────────────────────
  // Un USER_NORMAL sin sub activa NO debería poder trabajar → wizard
  // obligatorio para que termine el pago. Escenarios cubiertos:
  //   1. Completó plan/addons/support pero cerró Stripe sin pagar.
  //   2. Bug histórico: `isDone=true` sin haber pagado nunca.
  //   3. Sub cancelada/rechazada en cualquier momento después.
  //   4. Cualquier otro estado en que el user ya vio el bloque de
  //      facturación (o el wizard entero) pero no tiene sub activa.
  //
  // Regla: si YA HAY progress en BD (el user pasó por aquí) y NO
  // tiene sub → checkout pendiente. Cubre los 4 escenarios sin
  // depender de codes específicos en `completedSteps`, que podrían
  // no estar por bugs pasados o interrupciones.
  const hasActiveStripeSub = !!user?.paymentConfirmation?.[0]?.isActive;
  const hasProgressRow = !!progress; // fetch resolvió y creó fila
  const hasPendingCheckout =
    hasProgressRow
    && !hasActiveStripeSub
    && !isVeteran
    && (hasStartedBilling || progress?.isDone || alreadyStarted);

  // Es "nuevo" si el backend dice que aún no ha terminado el wizard
  // y no ha completado ningún step todavía. NO cruzamos con
  // `mesasCount` — la detección de "user veterano" (con mesas +
  // categorías + productos + antigüedad >30d) ya vive en el backend
  // (`GetOrCreateProgressAsync`), y si aplica, el backend responde
  // `isDone=true` directamente. Duplicar el check en front provocaba
  // que users legítimamente nuevos que tenían mesas de pruebas
  // anteriores no vieran el wizard.
  const isNewUser = isNotDone && !alreadyStarted;

  // Escape hatch admin: si `user.finishDemoTrial` es una fecha futura
  // significa que un admin de plataforma le concedió periodo de acceso
  // extendido (trial ampliado, cortesía, demo, cliente en negociación).
  // En ese caso el wizard sigue siendo visible/sugerido pero NO se
  // hace obligatorio — el user puede cerrarlo y trabajar. Sin este
  // check, cualquier licencia recién creada por el admin se
  // bloqueaba con el wizard sin posibilidad de saltar.
  const adminExtendedAccess =
    user?.finishDemoTrial && new Date(user.finishDemoTrial) > new Date();

  const isMandatory =
    (isNewUser || hasPendingCheckout) && !adminExtendedAccess && !isVeteran;

  // Chip visible: hay algo pendiente que atender. Veteranos NUNCA — su
  // licencia lleva tiempo en uso y no queremos empujarles al wizard.
  const isVisible =
    !isVeteran
    && ((isNotDone && (isNewUser || alreadyStarted))
      || hasPendingCheckout);

  // Auto-open: si obligatorio, siempre; si no, respeta "recordar mañana".
  // NUNCA auto-abrir mientras el gate legal está bloqueando la UI —
  // apilar el wizard encima confunde y roba foco al firmado obligatorio.
  //
  // Excepción admin-extended: cuando `user.finishDemoTrial > hoy` el
  // wizard NUNCA se auto-abre (ni siquiera al primer login) — solo se
  // abre manualmente desde el chip del TopBar. El admin le dio acceso
  // extendido a esta licencia, forzar el modal cada vez que cambia de
  // página es intrusivo. El chip sigue visible como recordatorio.
  const shouldAutoOpen = isVisible
    && !blockedByLegal
    && !adminExtendedAccess
    && (isMandatory || !progress?.dismissedForToday);

  // Users no elegibles (admins, franquiciados, comerciales, empleados,
  // distribuidores) NUNCA ven nada del wizard, aunque el progress
  // haya podido escribirse por error en algún flow anterior. Para el
  // consumer, esos users cuentan como "wizard completado" — no hay
  // nada pendiente que gate otros popups (notificaciones sistema, etc.).
  if (!isEligibleForWizard) {
    return {
      progress: null,
      loading: false,
      isVisible: false,
      shouldAutoOpen: false,
      isNewUser: false,
      isMandatory: false,
      hasPendingCheckout: false,
      wizardCompleted: true,
      blockedByLegal: false,
    };
  }

  return {
    progress,
    loading,
    isVisible,
    shouldAutoOpen,
    isNewUser,
    isMandatory,
    hasPendingCheckout,
    // Otros popups (notificaciones mandatorias, cookies, etc.) deben
    // gate-arse a esta señal para no aparecer encima del wizard.
    // wizardCompleted = true significa "no hay onboarding pendiente
    // ni checkout pendiente" — es seguro apilar otros modales.
    wizardCompleted: !isMandatory && !isVisible,
    blockedByLegal,
  };
}
